"use client";

import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

export function DashboardMock() {
  const t = useTranslations("landing.redesign.mock");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Box
      className="landing-dashboard-mock"
      bg="whiteAlpha.600"
      rounded="3xl"
      p={6}
      boxShadow="0 32px 80px rgba(0,0,0,0.12)"
      border="1px solid"
      borderColor="blackAlpha.100"
    >
      <Flex align="center" gap={2} mb={4}>
        <Box w={2} h={2} borderRadius="full" bg="error.400" />
        <Box w={2} h={2} borderRadius="full" bg="warning.400" />
        <Box w={2} h={2} borderRadius="full" bg="success.500" />
        <Text
          fontFamily="var(--font-landing-syne), sans-serif"
          fontSize="12px"
          fontWeight="600"
          color="neutral.900"
          ml={1}
        >
          {t("title")}
        </Text>
      </Flex>

      <Grid templateColumns="repeat(3, 1fr)" gap={2} mb={3.5}>
        <Box bg="neutral.50" borderRadius="10px" py={3} textAlign="center">
          <Text
            fontFamily="var(--font-landing-syne), sans-serif"
            fontSize="18px"
            fontWeight="700"
            color="neutral.900"
          >
            48
          </Text>
          <Text fontSize="10px" color="neutral.500" mt={0.5}>
            {t("stat_screened")}
          </Text>
        </Box>
        <Box bg="neutral.50" borderRadius="10px" py={3} textAlign="center">
          <Text
            fontFamily="var(--font-landing-syne), sans-serif"
            fontSize="18px"
            fontWeight="700"
            color="primary.500"
          >
            94%
          </Text>
          <Text fontSize="10px" color="neutral.500" mt={0.5}>
            {t("stat_match")}
          </Text>
        </Box>
        <Box bg="neutral.50" borderRadius="10px" py={3} textAlign="center">
          <Text
            fontFamily="var(--font-landing-syne), sans-serif"
            fontSize="18px"
            fontWeight="700"
            color="success.500"
          >
            12
          </Text>
          <Text fontSize="10px" color="neutral.500" mt={0.5}>
            {t("stat_shortlist")}
          </Text>
        </Box>
      </Grid>

      <Text
        fontSize="10px"
        fontWeight="600"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="neutral.500"
        mb={2}
      >
        {t("role_label")}
      </Text>

      <CandidateRow
        initials="HA"
        name={t("c1_name")}
        role={t("c1_role")}
        score="96"
        variant="high"
        avatarBg="primary.50"
        avatarColor="primary.700"
      />
      <CandidateRow
        initials="ML"
        name={t("c2_name")}
        role={t("c2_role")}
        score="88"
        variant="high"
        avatarBg="success.100"
        avatarColor="success.800"
      />
      <CandidateRow
        initials="TK"
        name={t("c3_name")}
        role={t("c3_role")}
        score="72"
        variant="mid"
        avatarBg="warning.100"
        avatarColor="warning.800"
      />

      <Box mt={3}>
        <BarRow
          label={t("skills_match")}
          value={86}
          color="primary.500"
          mounted={mounted}
        />
        <Box mt={1.5}>
          <BarRow
            label={t("exp_depth")}
            value={91}
            color="success.500"
            mounted={mounted}
          />
        </Box>
      </Box>
    </Box>
  );
}

function CandidateRow({
  initials,
  name,
  role,
  score,
  variant,
  avatarBg,
  avatarColor,
}: {
  initials: string;
  name: string;
  role: string;
  score: string;
  variant: "high" | "mid";
  avatarBg: string;
  avatarColor: string;
}) {
  return (
    <Flex
      align="center"
      gap={2.5}
      py={2.5}
      px={2.5}
      borderRadius="10px"
      mb={1.5}
      bg="neutral.50"
      transition="background 0.2s"
      _hover={{ bg: "primary.50" }}
    >
      <Flex
        w="32px"
        h="32px"
        borderRadius="full"
        bg={avatarBg}
        color={avatarColor}
        align="center"
        justify="center"
        fontSize="11px"
        fontWeight="700"
        fontFamily="var(--font-landing-syne), sans-serif"
        flexShrink={0}
      >
        {initials}
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontSize="12px" fontWeight="500" color="neutral.900">
          {name}
        </Text>
        <Text fontSize="10px" color="neutral.500">
          {role}
        </Text>
      </Box>
      <Text
        fontSize="11px"
        fontWeight="600"
        fontFamily="var(--font-landing-syne), sans-serif"
        px={2}
        py={0.5}
        borderRadius="full"
        bg={variant === "high" ? "success.100" : "warning.100"}
        color={variant === "high" ? "success.800" : "warning.800"}
      >
        {score}
      </Text>
    </Flex>
  );
}

function BarRow({
  label,
  value,
  color,
  mounted,
}: {
  label: string;
  value: number;
  color: string;
  mounted: boolean;
}) {
  return (
    <Box>
      <Flex justify="space-between" fontSize="10px" color="neutral.500" mb={1}>
        <span>{label}</span>
        <span>{value}%</span>
      </Flex>
      <Box h="4px" bg="neutral.100" borderRadius="full" overflow="hidden">
        <Box
          className="landing-bar-fill"
          h="full"
          bg={color}
          style={
            {
              "--bar-target": `${value}%`,
              width: mounted ? `${value}%` : "0%",
            } as React.CSSProperties
          }
        />
      </Box>
    </Box>
  );
}
